import { Centrifuge } from 'centrifuge'
import config from '@/assets/js/conf/config'
import { getCentrifugoToken } from '@/api/main/notification'

const CONNECTION_CACHE_KEY = 'merchantNotificationCentrifugoInfo'
const TOKEN_EXPIRE_BUFFER_SECONDS = 60
const HISTORY_LIMIT = 100

export const ConnectionState = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
  FAILED: 'failed'
}

function resolveEndpoint(endpoint) {
  // 后端会根据当前环境返回浏览器可访问的 Centrifugo 地址，优先使用接口结果。
  if (endpoint) {
    return endpoint
  }
  const envEndpoint = process.env.VUE_APP_CENTRIFUGO_WS_URL
  if (envEndpoint) {
    return envEndpoint
  }

  const base = config.baseApi || (window.location.protocol + '//' + window.location.host)
  return base.replace(/^http/, 'ws').replace(/\/$/, '') + '/connection/websocket'
}

function parseJwtPayload(token) {
  try {
    const payload = token.split('.')[1]
    if (!payload) {
      return null
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map(char => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

function isConnectionInfoValid(data, authToken) {
  if (!data || !data.token || !data.endpoint || !data.channel) {
    return false
  }
  if (data.authToken !== authToken) {
    return false
  }

  // Centrifugo 连接 token 带 exp 时，临近过期就重新向后端申请。
  const payload = parseJwtPayload(data.token)
  if (payload && payload.exp) {
    return payload.exp > Math.floor(Date.now() / 1000) + TOKEN_EXPIRE_BUFFER_SECONDS
  }

  return true
}

function getCachedConnectionInfo(authToken) {
  try {
    const raw = sessionStorage.getItem(CONNECTION_CACHE_KEY)
    const data = raw ? JSON.parse(raw) : null
    return isConnectionInfoValid(data, authToken) ? data : null
  } catch (e) {
    return null
  }
}

function setCachedConnectionInfo(data, authToken) {
  try {
    sessionStorage.setItem(CONNECTION_CACHE_KEY, JSON.stringify({
      ...data,
      authToken
    }))
  } catch (e) {
    // 缓存失败不影响实时通知连接。
  }
}

export function clearMerchantNotificationConnectionCache() {
  try {
    sessionStorage.removeItem(CONNECTION_CACHE_KEY)
  } catch (e) {
    // ignore
  }
}

function pickConnectionInfo(res) {
  // request 封装通常返回 { code, data }，这里也兼容 axios 原始 { data: { code, data } }。
  if (res && res.token) {
    return res
  }
  if (res && res.data && res.data.token) {
    return res.data
  }
  if (res && res.data && res.data.data && res.data.data.token) {
    return res.data.data
  }
  return null
}

async function fetchConnectionInfo({ useCache = true } = {}) {
  const authToken = localStorage.getItem(config.tokenKey) || ''
  if (useCache) {
    const cached = getCachedConnectionInfo(authToken)
    if (cached) {
      return cached
    }
  }

  const res = await getCentrifugoToken()
  const data = pickConnectionInfo(res)

  if (!data || !data.token || !data.endpoint || !data.channel) {
    throw new Error('通知连接信息异常，请检查 centrifugo-token 接口返回')
  }

  setCachedConnectionInfo(data, authToken)
  return data
}

export function createMerchantNotificationWS({ handlers = {} }) {
  const {
    onConnected,
    onNotification,
    onError,
    onClose,
    onStateChange,
  } = handlers

  let client = null
  let subscription = null
  let closedManually = false
  let reconnectAttempts = 0
  let currentState = ConnectionState.DISCONNECTED

  function setState(newState) {
    if (currentState !== newState) {
      currentState = newState
      onStateChange && onStateChange(newState, reconnectAttempts)
    }
  }

  function handlePublication(ctx, source = 'live') {
    const data = ctx && ctx.data ? ctx.data : {}
    // 后端统一发布 { type, payload }，兼容直接发送业务 payload 的情况。
    const type = data.type || data.payload?.type || 'message'
    const payload = data.payload || data
    onNotification && onNotification({ type, payload, source })
  }

  async function loadRecentHistory() {
    if (!subscription || typeof subscription.history !== 'function') {
      return
    }

    try {
      const ctx = await subscription.history({
        limit: HISTORY_LIMIT,
        reverse: true
      })
      const publications = Array.isArray(ctx.publications) ? ctx.publications : []

      // history 使用 reverse=true 取最近 N 条，返回通常是新到旧；倒序写入可保持列表最新在前。
      publications.slice().reverse().forEach(publication => {
        handlePublication(publication, 'history')
      })
    } catch (e) {
      onError && onError(e.message || '通知历史恢复失败')
    }
  }

  async function connect() {
    if (closedManually) {
      return
    }

    setState(reconnectAttempts === 0 ? ConnectionState.CONNECTING : ConnectionState.RECONNECTING)

    try {
      const data = await fetchConnectionInfo()
      const endpoint = resolveEndpoint(data.endpoint)

      client = new Centrifuge(endpoint, {
        token: data.token,
        getToken: async () => {
          const refreshData = await fetchConnectionInfo({ useCache: false })
          return refreshData.token
        }
      })

      client.on('connecting', () => {
        reconnectAttempts += currentState === ConnectionState.CONNECTED ? 1 : 0
        setState(reconnectAttempts > 0 ? ConnectionState.RECONNECTING : ConnectionState.CONNECTING)
      })

      client.on('connected', (ctx) => {
        reconnectAttempts = 0
        setState(ConnectionState.CONNECTED)
        onConnected && onConnected({
          status: 'ok',
          channel: data.channel,
          client: ctx.client,
          transport: ctx.transport
        })
      })

      client.on('disconnected', (ctx) => {
        if (closedManually) {
          setState(ConnectionState.DISCONNECTED)
          onClose && onClose(ctx)
          return
        }

        setState(ctx && ctx.reason === 'unauthorized' ? ConnectionState.FAILED : ConnectionState.RECONNECTING)
      })

      client.on('error', (ctx) => {
        onError && onError(ctx?.error?.message || ctx?.message || '通知连接异常', ctx?.error?.code)
      })

      subscription = client.newSubscription(data.channel)

      subscription.on('publication', handlePublication)
      subscription.on('subscribing', () => {
        if (!closedManually && currentState === ConnectionState.CONNECTED) {
          setState(ConnectionState.RECONNECTING)
        }
      })
      subscription.on('subscribed', () => {
        setState(ConnectionState.CONNECTED)
        loadRecentHistory()
      })
      subscription.on('unsubscribed', (ctx) => {
        if (!closedManually) {
          onError && onError(ctx?.reason || '通知订阅已断开', ctx?.code)
          setState(ConnectionState.FAILED)
        }
      })
      subscription.on('error', (ctx) => {
        onError && onError(ctx?.error?.message || ctx?.message || '通知订阅异常', ctx?.error?.code)
      })

      subscription.subscribe()
      client.connect()
    } catch (e) {
      setState(ConnectionState.FAILED)
      onError && onError(e.message || '通知连接创建失败')
    }
  }

  connect()

  return {
    getState() {
      return currentState
    },

    getReconnectAttempts() {
      return reconnectAttempts
    },

    reconnect() {
      closedManually = false
      reconnectAttempts = 0
      if (client) {
        client.disconnect()
      }
      connect()
    },

    close() {
      closedManually = true
      if (subscription) {
        subscription.unsubscribe()
        subscription = null
      }
      if (client) {
        client.disconnect()
        client = null
      }
      setState(ConnectionState.DISCONNECTED)
    },
  }
}
